import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { Box, Button, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { selectRole } from "src/redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "src/redux/hooks/redux";
import { getBasePath } from "src/utils";
import { colors } from "../ui/colors";

export interface WidgetBenefitBlockedStateProps {
  title?: string;
  message?: string;
  actionText?: string;
  onAction?: () => void;
  height?: number | string;
  compact?: boolean;
  sx?: any;
}

export function isBenefitForbiddenError(error: any): boolean {
  if (!error) return false;
  return (
    error?.response?.status === 403 ||
    error?.status === 403 ||
    error?.response?.data?.status === 403 ||
    error?.response?.data?.statusCode === 403 ||
    error?.statusCode === 403
  );
}

export function getBenefitErrorMessage(error: any, fallbackMessage?: string): string {
  const rawMessage = error?.response?.data?.message || error?.message;

  if (rawMessage && typeof rawMessage === "string") {
    if (rawMessage.toLowerCase().includes("do not have this benefit")) {
      return "Your current plan does not include this feature.";
    }
    return rawMessage;
  }

  return fallbackMessage || "Your current plan does not include this feature.";
}

export const WidgetBenefitBlockedState: React.FC<WidgetBenefitBlockedStateProps> = ({
  title = "Feature Locked",
  message = "Your current plan does not include this feature.",
  actionText = "Upgrade Plan",
  onAction,
  height = "100%",
  compact = false,
  sx = {},
}) => {
  const navigate = useNavigate();
  const roleType = useAppSelector(selectRole);
  const basePath = getBasePath(roleType);

  const handleAction = () => {
    if (onAction) {
      onAction();
    } else {
      navigate(`${basePath}/plan-center`);
    }
  };

  const displayMessage = message.includes("do not have this benefit") ? "Your account plan does not include this benefit." : message;

  return (
    <Box
      sx={{
        width: "100%",
        height: height,
        minHeight: compact ? 130 : 160,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: compact ? 1.5 : 2.5,
        borderRadius: "16px",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        border: "1px dashed var(--fx-surface-border, #E2E8F0)",
        textAlign: "center",
        position: "relative",
        boxSizing: "border-box",
        ...sx,
      }}
    >
      <Box
        sx={{
          width: compact ? 36 : 44,
          height: compact ? 36 : 44,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(252, 151, 0, 0.1)",
          color: colors.orange[500] || "#fc9700",
          mb: compact ? 1 : 1.5,
          border: "1px solid rgba(252, 151, 0, 0.2)",
          boxShadow: "0 2px 8px rgba(252, 151, 0, 0.12)",
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: compact ? 20 : 24 }} />
      </Box>

      {/* Title */}
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color: "var(--fx-surface-text, #0F172A)",
          fontSize: compact ? "0.8125rem" : "0.875rem",
          lineHeight: 1.2,
          mb: 0.5,
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: "var(--fx-surface-textsecondary, #64748B)",
          fontSize: compact ? "0.7rem" : "0.75rem",
          maxWidth: 280,
          lineHeight: 1.35,
          mb: compact ? 1.5 : 2,
        }}
      >
        {displayMessage}
      </Typography>

      <Button
        variant="contained"
        size="small"
        disableElevation
        onClick={handleAction}
        startIcon={<WorkspacePremiumIcon sx={{ fontSize: "16px !important" }} />}
        sx={{
          bgcolor: colors.orange[500] || "#fc9700",
          color: "#ffffff",
          fontWeight: 700,
          fontSize: "0.75rem",
          textTransform: "none",
          borderRadius: "8px",
          px: 2,
          py: 0.5,
          boxShadow: "0 2px 6px rgba(252, 151, 0, 0.25)",
          "&:hover": {
            bgcolor: colors.orange[600] || "#e58900",
            boxShadow: "0 4px 12px rgba(252, 151, 0, 0.35)",
          },
        }}
      >
        {actionText}
      </Button>
    </Box>
  );
};
